import React, { useState, useEffect, Suspense, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cards from "../common/Cards";
import DynamicTable from "../common/DynamicTable";
import SearchFilter from "../common/SearchFilter";
import SubTitle from "../common/SubTitle";
import NavMenu from "../navbar/NavMenu";
import TitleBar from "../navbar/TitleBar";
import DeleteModal from "../common/DeleteModal";
import config from "../../config.js";
import Update_Icon from "../../images/table/Update_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import Assign_Icon from "../../images/table/Assign_icon.svg";
import Active_Icon from "../../images/organization/Active_Icon.svg";
import Inactive_Icon from "../../images/organization/Inactive_Icon.svg";
import AccessDenied from "./Acessdenied";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import StatusToggle from "./StatusToggle.jsx";
import Footer from "../common/Footer.jsx";
import { useTranslation } from "react-i18next";

export default function OrganizationList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    Organization: "",
    City: "",
    Status: "",
  });
  const [regData, setRegData] = useState([]);

  const orgData = localStorage.getItem("userState");
  const parsedOrgData = JSON.parse(orgData);
  const orgId = parsedOrgData?.user?.organizationId;
  const orgName = parsedOrgData?.user?.name;
  const orgEmail = parsedOrgData?.user?.email;
  console.log("id", orgId, orgName);

  // Enhanced actions for the table
  const actions = [
    {
      id: "update",
      label: t("Update"),
      icon: Update_Icon,
      className: "update-action",
    },
    {
      id: "assign",
      label: t("Assign"),
      icon: Assign_Icon,
      className: "assign-action",
    },
  ];

  // Dynamic filter configuration
  const [filterConfig, setFilterConfig] = useState([
    {
      id: "Organization",
      label: "organization",
      helperText: "Select organization",
      options: [],
    },
    {
      id: "City",
      label: "city",
      helperText: "Select city",
      options: [],
    },
    {
      id: "Status",
      label: "status",
      helperText: "Select status",
      options: [
        { value: "ACTIVE", label: "Active" },
        { value: "INACTIVE", label: "Inactive" },
        // { value: 'DEAD', label: 'Dead' }
      ],
    },
  ]);

  // const headers = [
  //   'name',
  //   'domain',
  //   'city',
  //   'contactName',
  //   'status',
  //   'Actions'
  // ];
  const headers = [
    { key: "name", value: "name" },
    { key: "domain", value: "domain" },
    { key: "city", value: "city" },
    { key: "contactName", value: "contactName" },
    { key: "status", value: "status" },
    { key: ACTIONS_COLUMN_ID, value: "Actions" },
  ];

  // Fetch organizations and populate dynamic filter options
  useEffect(() => {
    fetchOrganizations();
    fetchRequest();
  }, []);

  const fetchRequest = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/register`);
      console.info(response, "response");
      setRegData(response?.data?.organizations || 0);
    } catch (error) {
      console.log(error);
    }
  };
  console.log(regData, "regdata");

  const handleStatusChange = async (organizationId, newStatus) => {
    try {
      console.log(
        `Changing status for organization ${organizationId} to ${newStatus}`
      );
      // Call your backend API with PUT method
      await axios.put(`${config.baseURL}/organization/${organizationId}`, {
        organizationStatus: newStatus,
      });

      // Update local state after successful API call
      setOrganizations((prevOrgs) =>
        prevOrgs.map((org) =>
          org.id === organizationId
            ? {
                ...org,
                status: newStatus,
                statusDisplay: (
                  <StatusToggle
                    initialStatus={newStatus}
                    organizationId={organizationId}
                    onStatusChange={handleStatusChange}
                  />
                ),
              }
            : org
        )
      );

      // Also update filtered organizations to reflect changes immediately
      setFilteredOrganizations((prevOrgs) =>
        prevOrgs.map((org) =>
          org.id === organizationId
            ? {
                ...org,
                status: newStatus,
                statusDisplay: (
                  <StatusToggle
                    initialStatus={newStatus}
                    organizationId={organizationId}
                    onStatusChange={handleStatusChange}
                  />
                ),
              }
            : org
        )
      );

      console.log(
        `Organization ${organizationId} status updated to ${newStatus}`
      );
    } catch (error) {
      console.error("Error updating organization status:", error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);

      const [orgResponse, registerResponse] = await Promise.all([
        axios.get(`${config.baseURL}/organization`),
        axios.get(`${config.baseURL}/register`),
      ]);

      const organizationsData = orgResponse.data.organizations || [];
      const registerData = registerResponse.data.organizations || [];

      const approvedOrgIds = new Set(
        registerData
          .filter((reg) => reg.approvalStatus === "ACCEPTED")
          .map((reg) => reg.organizationId)
      );

      const allRegisterOrgIds = new Set(
        registerData.map((reg) => reg.organizationId)
      );

      const filteredOrgs = organizationsData.filter((org) => {
        const existsInRegister = allRegisterOrgIds.has(org.id);
        const isApproved = approvedOrgIds.has(org.id);
        return !existsInRegister || isApproved;
      });

      const transformedData = filteredOrgs.map((org) => ({
        id: org.id,
        name: org.name,
        domain: org.domain,
        city: org.city || "N/A",
        contactName: org.contactName || "N/A",
        status: org.organizationStatus,
        statusDisplay: (
          <StatusToggle
            initialStatus={org.organizationStatus}
            organizationId={org.id}
            onStatusChange={handleStatusChange}
          />
        ),
      }));

      setOrganizations(transformedData);
      setFilteredOrganizations(transformedData);

      const uniqueOrganizations = [
        ...new Set(filteredOrgs.map((org) => org.name).filter(Boolean)),
      ];
      const uniqueCities = [
        ...new Set(filteredOrgs.map((org) => org.city).filter(Boolean)),
      ];

      setFilterConfig((prevConfig) =>
        prevConfig.map((filter) => {
          if (filter.id === "Organization") {
            return {
              ...filter,
              options: uniqueOrganizations.map((name) => ({
                value: name,
                label: name,
              })),
            };
          }
          if (filter.id === "City") {
            return {
              ...filter,
              options: uniqueCities.map((city) => ({
                value: city,
                label: city,
              })),
            };
          }
          return filter;
        })
      );

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setError(error);
      setIsLoading(false);
    }
  };

  // Enhanced search and filter handler
  useEffect(() => {
    // Apply filtering whenever search term or selected filters change
    const applyFilters = () => {
      let result = [...organizations]; // Create a fresh copy of organizations array

      // Apply search
      if (searchTerm && searchTerm.trim() !== "") {
        result = result.filter((org) =>
          Object.entries(org).some(([key, value]) => {
            // Skip non-string/number values and statusDisplay in search
            if (
              key === "statusDisplay" ||
              key === "id" ||
              typeof value === "object"
            ) {
              return false;
            }
            return String(value)
              .toLowerCase()
              .includes(searchTerm.toLowerCase().trim());
          })
        );
      }

      // Apply individual filters
      if (selectedFilters.Organization && selectedFilters.Organization !== "") {
        result = result.filter(
          (org) => org.name === selectedFilters.Organization
        );
      }

      if (selectedFilters.City && selectedFilters.City !== "") {
        result = result.filter((org) => org.city === selectedFilters.City);
      }

      if (selectedFilters.Status && selectedFilters.Status !== "") {
        result = result.filter((org) => org.status === selectedFilters.Status);
      }

      console.log("Filtered results:", result);
      console.log("Applied filters:", selectedFilters);

      setFilteredOrganizations(result);
    };

    applyFilters();
  }, [searchTerm, selectedFilters, organizations]);

  // Handle search change - separate handler for search
  const handleSearchChange = (value) => {
    console.log("Search value changed:", value);
    setSearchTerm(value || "");
  };

  // Handle filter change - separate handler for filters
  const handleFilterChange = (filterValues) => {
    console.log("Filter values changed:", filterValues);
    setSelectedFilters(filterValues);
  };

  const handleAddOrganization = () => {
    navigate("/organizationUpdate");
  };

  // Handle action clicks
  const handleActionClick = (actionId, rowIndex, actionObj) => {
    const selectedOrg = filteredOrganizations[rowIndex];

    switch (actionId) {
      case "update":
        navigate(`/organizationUpdate/${selectedOrg.id}`);
        break;
      // case "delete":
      //   setOrganizationToDelete(selectedOrg);
      //   setDeleteModalOpen(true);
      //   break;
      case "assign":
        console.log("Assign features to organization:", selectedOrg);
        navigate(`/organization/feature/${selectedOrg.id}`);
        break;
      default:
        console.log("Unknown action:", actionId);
    }
  };

  // Card data
  const cardData = [
    {
      title: "totalOrganizations",
      value: organizations.length,
      backgroundColor: "#183059",
      titleColor: "#FFFFFF",
      valueColor: "#FFFFFF",
    },
    {
      title: "activeOrganizations",
      value: organizations.filter((org) => org.status === "ACTIVE").length,
      backgroundColor: "#008C23",
      titleColor: "#FFFFFF",
      valueColor: "#FFFFFF",
    },
    {
      title: "inactiveOrganizations",
      value: organizations.filter((org) => org.status === "INACTIVE").length,
      backgroundColor: "#EE3B2B",
      titleColor: "#FFFFFF",
      valueColor: "#FFFFFF",
    },
    {
      title: "newRequest",
      value: regData.filter((org) => org.approvalStatus === "PENDING").length,
      backgroundColor: "#20A8E0",
      titleColor: "#FFFFFF",
      valueColor: "#FFFFFF",
    },
  ];

  // Deletion handler
  const handleDeleteConfirm = async () => {
    if (organizationToDelete) {
      try {
        console.log("Deleting organization:", organizationToDelete);
        await axios.delete(
          `${config.baseURL}/organization/${organizationToDelete.id}`
        );
        console.log("Organization deleted successfully:", organizationToDelete);
        fetchOrganizations();
      } catch (error) {
        console.error("Error deleting organization:", error);
      } finally {
        setDeleteModalOpen(false);
        setOrganizationToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setOrganizationToDelete(null);
  };

  const tableData = useMemo(() => {
    return filteredOrganizations.map((org) => {
      const displayData = { ...org };
      displayData.status = org.statusDisplay;
      return displayData;
    });
  }, [filteredOrganizations]);

  if (error) {
    return <div>{t("Error")}: {error.message}</div>;
  }

  if (orgId !== 5 || orgEmail !== "sales@routeeye.io") {
    return <AccessDenied />;
  }

  console.log("tableData being rendered:", tableData);
  console.log("Current filters:", selectedFilters);
  console.log("Filtered organizations count:", filteredOrganizations.length);

  const orgSubtitleName = JSON.parse(localStorage.getItem("userState"))
    ?.organization?.name;

  return (
    <>
      <TitleBar />
      <NavMenu />
      <SubTitle
        title="organizations"
        // path="organization(Irisidea) > allOrganizations"
        path={`${orgSubtitleName} > Organizations`}
        add="createOrganization"
        addPath=""
        onAddClick={handleAddOrganization}
      />
      <Cards
        cardData={cardData}
        cardPaths={{
          newRequest: "/admin",
        }}
      />
      <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="Search organizations"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />
        <Suspense fallback={<div className="loader">{t("Loading...")}</div>}>
          <DynamicTable
            headers={headers}
            data={tableData}
            loading={isLoading}
            actions={actions}
            onActionClick={handleActionClick}
            key={JSON.stringify(selectedFilters)} // Add a key to force re-render
          />
        </Suspense>
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Organization"
        message={`${t("Are you sure you want to delete")} ${
          organizationToDelete?.name || t("this organization")
        }? ${t("This action cannot be undone.")}`}
      />
      <Footer />
    </>
  );
}
